import React, { useMemo, useState, useCallback } from 'react';
import { Card,  Badge, Dropdown, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatDateWithLocale, sanitizeHTML } from '../../shared/utils';
import { isDayWithinAccommodationRange, findMultiDayAccommodations } from '../../shared/activityUtils';
import { buildTransportationSegments } from '../../shared/transportationSegments';
import { generateShareUrl } from '../utils/shareUtils';
import { renderActivityShare } from './activities/indexShare';
import MinimizedDayShare from './MinimizedDayShare';
import { QRCodeSVG } from 'qrcode.react';
import '../../components/InfoTab.css';

function DaysTabShare({ parsedData, isMinimized, onOpenDayMap, shareId, planLanguage, isTemplate, onToggleMinimize }) {
  const { t } = useTranslation();
  const summaryStorageKey = shareId ? `tripsy_share_day_summary_${shareId}` : null;
  const [summaryCollapsedOverrides, setSummaryCollapsedOverrides] = useState(() => {
    if (!summaryStorageKey) return {};
    try {
      const raw = localStorage.getItem(summaryStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const isDaySummarySectionExpanded = useCallback((day) => {
    if (!day) return true;
    if (Object.prototype.hasOwnProperty.call(summaryCollapsedOverrides, day.id)) {
      return summaryCollapsedOverrides[day.id] !== true;
    }
    return day.summaryCollapsed !== true;
  }, [summaryCollapsedOverrides]);

  const toggleDaySummarySection = (dayId) => {
    const day = parsedData?.days?.find((d) => d.id === dayId);
    if (!day) return;

    const nextCollapsed = isDaySummarySectionExpanded(day);
    const nextOverrides = { ...summaryCollapsedOverrides, [dayId]: nextCollapsed };
    setSummaryCollapsedOverrides(nextOverrides);
    if (summaryStorageKey) {
      localStorage.setItem(summaryStorageKey, JSON.stringify(nextOverrides));
    }
  };

  // Same next-activity map as PlanDetail: used for "Next Stop" Google Directions link
  const nextActivityMap = useMemo(() => {
    if (!parsedData) return new Map();
    const { nextActivityMap: map } = buildTransportationSegments(parsedData);
    return map;
  }, [parsedData]);

  // Render a simplified version of accommodation for continued days
  const renderContinuedAccommodation = (activity) => {
    return (
      <Card className="mb-2 shadow-sm border-success">
        <Card.Body className="py-2">
          <div className="d-flex align-items-center">
            <i className="bi bi-house-door me-2 text-success"></i>
            <span><strong>{t('planDetailShare.stayingAt')}:</strong> {activity.name}</span>
          </div>
        </Card.Body>
      </Card>
    );
  };
  
  if (!parsedData) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <i className="bi bi-exclamation-triangle display-1 text-muted mb-3"></i>
          <h4 className="text-muted">No plan data</h4>
          <p className="text-muted">Unable to load plan information.</p>
        </Card.Body>
      </Card>
    );
  }

  if (!parsedData.days || parsedData.days.length === 0) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <i className="bi bi-calendar-x display-1 text-muted mb-3"></i>
          <h4 className="text-muted">No days found</h4>
          <p className="text-muted">This plan doesn't have any days configured.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="days-tab-share">
      {/* Plan Header */}
      <Card className="mb-1">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="flex-grow-1">
              <h4 className="mb-3">{parsedData.name || t('planDetailShare.tripPlan')}</h4>
              {parsedData.description ? (
                <div className="info-content mb-0" dangerouslySetInnerHTML={{ __html: sanitizeHTML(parsedData.description) }} />
              ) : (
                <p className="mb-0 text-muted">{t('planDetailShare.noDescription')}</p>
              )}
            </div>
            
            {/* QR Code - Desktop only (hidden on mobile) */}
            {shareId && (
              <div className="text-center ms-3 flex-shrink-0 d-none d-md-block">
                <small className="text-muted d-block mb-2">{t('planDetailShare.scanToViewPlan')}</small>
                <QRCodeSVG
                  value={`${window.location.origin}/share/plan/${shareId}`}
                  size={120}
                  level="H"
                  includeMargin={true}
                  imageSettings={{
                    src: '/logo.svg',
                    height: 24,
                    width: 24,
                    excavate: true,
                  }}
                />
              </div>
            )}
          </div>
          
          {/* Display destination images if available */}
          {parsedData.images && Array.isArray(parsedData.images) && parsedData.images.length > 0 && (
            <div className="destination-images mt-3 mb-4" style={{ width: 'fit-content' }}>
              {/* Mobile: Show only first 2 images */}
              <div className="d-md-none">
                <div className="d-flex flex-wrap gap-2">
                  {parsedData.images.slice(0, 2).map((image, index) => {
                    // Skip rendering if image is null, undefined, or not a valid format
                    if (!image || (typeof image !== 'string' && !image.url)) {
                      return null;
                    }
                    
                    // Get the attribution text
                    const attributionText = typeof image.attribution === 'string' 
                      ? image.attribution
                      : (image.attribution?.name || '');
                    
                    // Truncate the attribution text
                    const truncatedAttribution = attributionText.length > 20 
                      ? attributionText.substring(0, 20) + '...'
                      : attributionText;
                    
                    // Only show attribution if it has a value
                    const hasAttribution = truncatedAttribution && truncatedAttribution.trim().length > 0;
                    
                    return (
                      <div 
                        key={index} 
                        className="position-relative"
                        style={{ 
                          flex: '0 0 auto',
                          maxWidth: '180px'
                        }}
                      >
                        <img 
                          src={typeof image === 'string' ? image : image.url} 
                          alt={typeof image === 'string' ? `Destination image ${index + 1}` : (image.alt || `Destination image ${index + 1}`)}
                          className="img-thumbnail"
                          style={{ 
                            height: '180px',
                            width: '180px',
                            objectFit: 'contain',
                            backgroundColor: '#f8f9fa'
                          }}
                        />
                        {typeof image !== 'string' && hasAttribution && (
                          <div className="text-muted small text-center mt-1" style={{ fontSize: '0.7rem' }}>
                            <small title={attributionText}>
                              {t('planDetail.overview.by')} {truncatedAttribution}
                            </small>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Desktop: Show all images */}
              <div className="d-none d-md-flex flex-wrap gap-2">
                {parsedData.images.map((image, index) => {
                  // Skip rendering if image is null, undefined, or not a valid format
                  if (!image || (typeof image !== 'string' && !image.url)) {
                    return null;
                  }
                  
                  // Get the attribution text
                  const attributionText = typeof image.attribution === 'string' 
                    ? image.attribution
                    : (image.attribution?.name || '');
                  
                  // Truncate the attribution text
                  const truncatedAttribution = attributionText.length > 20 
                    ? attributionText.substring(0, 20) + '...'
                    : attributionText;
                  
                  // Only show attribution if it has a value
                  const hasAttribution = truncatedAttribution && truncatedAttribution.trim().length > 0;
                  
                  return (
                    <div 
                      key={index} 
                      className="position-relative"
                      style={{ 
                        flex: '0 0 auto',
                        maxWidth: '180px'
                      }}
                    >
                      <img 
                        src={typeof image === 'string' ? image : image.url} 
                        alt={typeof image === 'string' ? `Destination image ${index + 1}` : (image.alt || `Destination image ${index + 1}`)}
                        className="img-thumbnail"
                        style={{ 
                          height: '180px',
                          width: '180px',
                          objectFit: 'contain',
                          backgroundColor: '#f8f9fa'
                        }}
                      />
                      {typeof image !== 'string' && hasAttribution && (
                        <div className="text-muted small text-center mt-1" style={{ fontSize: '0.7rem' }}>
                          <small title={attributionText}>
                            {t('planDetail.overview.by')} {truncatedAttribution}
                          </small>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {parsedData.imagesProvider && (
                <div className="text-end mt-1">
                  <small className="text-muted">{t('planDetailShare.imagesProvidedBy')} {typeof parsedData.imagesProvider === 'string' 
                    ? parsedData.imagesProvider 
                    : parsedData.imagesProvider.name || t('planDetailShare.imageProvider')}</small>
                </div>
              )}
            </div>
          )}
          
          {/* QR Code - Mobile only (below images) */}
          {shareId && (
            <div className="text-center mt-3 d-md-none">
              <small className="text-muted d-block mb-2">{t('planDetailShare.scanToViewPlan')}</small>
              <QRCodeSVG
                value={generateShareUrl(shareId, { language: planLanguage })}
                size={120}
                level="H"
                includeMargin={true}
                imageSettings={{
                  src: '/logo.svg',
                  height: 24,
                  width: 24,
                  excavate: true,
                }}
              />
            </div>
          )}
        </Card.Body>
      </Card>
      
      {/* Minimize/Expand Button - Before Days section */}
      {parsedData.days && parsedData.days.length > 0 && onToggleMinimize && (
        <div className="d-flex justify-content-end mb-3 mt-3">
          <Button 
            variant="outline-secondary" 
            onClick={onToggleMinimize}
            title={isMinimized ? "Expand View" : "Minimize View"}
            size="sm"
          >
            <i className={`bi ${isMinimized ? 'bi-arrows-angle-expand' : 'bi-arrows-angle-contract'}`}></i>
            <span className="ms-1">{isMinimized ? "Expand" : "Minimize"}</span>
          </Button>
        </div>
      )}
      
      {parsedData.days.map((day, index) => {
        const dayNumber = index + 1;
        const activities = day.activities || [];
        
        // Get any multi-day accommodations
        const multiDayAccoms = findMultiDayAccommodations(parsedData);
        const continueAccoms = multiDayAccoms.filter(accom => 
          isDayWithinAccommodationRange(day.date, accom.startDateTime, accom.endDateTime) &&
          accom.originalDay !== day.date
        );
        
        // Return minimized or full view based on state
        return isMinimized ? (
          <MinimizedDayShare
            key={day.id || index}
            day={day}
            dayIndex={index}
            continueAccoms={continueAccoms}
            isTemplate={isTemplate}
          />
        ) : (
          <Card key={day.id || index} className="mb-4" id={`day-${day.id}`}>
            <Card.Header className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 d-none d-md-block">
                  <i className="bi bi-calendar-date me-2"></i>
                  {t('planDetailShare.dayLabel', { number: dayNumber })}{isTemplate ? '' : `: ${day.date ? formatDateWithLocale(day.date, 'PPP') : ''}`}
                </h5>
                <h6 className="mb-0 d-md-none">
                  <i className="bi bi-calendar-date me-1"></i>
                  {t('planDetailShare.dayLabel', { number: dayNumber })}{isTemplate ? '' : `: ${day.date ? formatDateWithLocale(day.date, 'EEE, MMM d') : ''}`}
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="light" text="dark" className="ms-2">
                    {activities.length} {activities.length === 1 ? t('planDetailShare.activity') : t('planDetailShare.activities')}
                  </Badge>
                  <Dropdown>
                    <Dropdown.Toggle 
                      variant="primary" 
                      id={`dropdown-day-actions-share-${day.id}`}
                      size="sm"
                    >
                      <i className="bi bi-gear"></i>
                      <span className="d-none d-sm-inline ms-1">{t('planDetailShare.dayActions')}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      <Dropdown.Item onClick={() => onOpenDayMap && onOpenDayMap(day, index)}>
                        <i className="bi bi-map me-2"></i>
                        {t('planDetailShare.map')}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </Card.Header>
            
            <Card.Body>
              {/* Display continuing accommodations at the top */}
              {continueAccoms.length > 0 && (
                <div className="mb-3">
                  {continueAccoms.map((accom, idx) => (
                    <div key={`continued-${idx}`}>
                      {renderContinuedAccommodation(accom)}
                    </div>
                  ))}
                </div>
              )}

              {/* Day summary (read-only on share, collapsible) */}
              {(day.summaryTitle?.trim() || day.summary?.trim()) && (
                <div className="mb-3">
                  <Card className="border-0 bg-light shadow-sm">
                    <div
                      className="d-flex align-items-center gap-2 py-2 px-3"
                      onClick={() => toggleDaySummarySection(day.id)}
                      style={{ cursor: 'pointer' }}
                      role="button"
                      aria-expanded={isDaySummarySectionExpanded(day)}
                    >
                      <i className={`bi ${isDaySummarySectionExpanded(day) ? 'bi-chevron-down' : 'bi-chevron-right'} flex-shrink-0`}></i>
                      <h6 className="mb-0 fw-semibold text-dark flex-grow-1 min-w-0">
                        {day.summaryTitle?.trim() || t('planDetail.days.daySummary.title', { defaultValue: 'Day summary' })}
                      </h6>
                    </div>
                    {isDaySummarySectionExpanded(day) && day.summary?.trim() && (
                      <Card.Body className="pt-0 pb-2 px-3">
                        <p className="mb-0 text-muted small" style={{ whiteSpace: 'pre-wrap' }}>{day.summary.trim()}</p>
                      </Card.Body>
                    )}
                  </Card>
                </div>
              )}

              {activities.length > 0 ? (
                <div className="activities-list">
                  {activities.map((activity, activityIndex) => {
                    const nextActivityInfo = nextActivityMap.get(activity.id) || null;
                    return (
                      <div key={activity.id || activityIndex} className="mb-3">
                        {renderActivityShare(activity, day.id, nextActivityInfo)}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="bi bi-clock text-muted display-4 mb-3"></i>
                  <h6 className="text-muted">{t('planDetailShare.noActivitiesPlanned')}</h6>
                  <p className="text-muted small">{t('planDetailShare.noActivitiesScheduled')}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
}

export default DaysTabShare;
